import React, { useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Icon,
  useColorModeValue,
  Stack,
  List,
  ListItem,
  ListIcon,
  Divider,
  Image,
  Link,
  Center,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Input,
  Textarea,
  Select,
  useToast,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import {
  FaRocket,
  FaMicrochip,
  FaServer,
  FaCloud,
  FaChartLine,
  FaShieldAlt,
  FaUsers,
  FaCheck,
  FaArrowRight,
  FaPlay,
  FaCog,
  FaDatabase,
  FaGlobe,
  FaLock,
  FaBolt,
  FaSignOutAlt,
  FaCar,
  FaMicrochip as FaElectronics,
  FaBolt as FaEnergy,
  FaCheckCircle,
  FaWrench,
  // FaBrain, // Copilot temporarily hidden
  FaClock,
  FaCreditCard, 
  FaTimes,
  FaTint,
  FaGift,
  FaBookOpen,
  FaCalendarAlt,
  FaRobot,
  FaCoins,
  FaFileAlt,
  FaPlane,
  FaFan,
  FaWind,
  FaVolumeUp,
  FaShip,
  FaBuilding,
  FaLeaf,
  FaAnchor,
  FaOilCan,
  FaCircleNotch,
  FaAtom,
  FaBroadcastTower,
  FaMobileAlt,
  FaHeartbeat,
  FaFlask,
} from 'react-icons/fa';
import {
  IconAtom,
  IconGridDots,
  IconWaveSawTool,
} from "@tabler/icons-react";

import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';
import NavDropdowns from './NavDropdowns';
import Turnstile from "react-turnstile";
import { TURNSTILE_SITE_KEY } from "../config";
import { CfdWavesIcon, CemPalaceIcon } from './icons/UseCaseIcons';

const FeatureCard = ({ icon, title, description, color }) => {
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <Card bg={cardBg} border="1px" borderColor={borderColor} shadow="md" h="full">
      <CardBody p={6}>
        <VStack spacing={4} align="start">
          <Icon as={icon} color={color} boxSize={8} />
          <Heading size="md" color="gray.800">
            {title}
          </Heading>
          <Text color="gray.600" fontSize="sm">
            {description}
          </Text>
        </VStack>
      </CardBody>
    </Card>
  );
};

const PricingCard = ({ title, price, priceSubline, ctaLabel, features, popular = false, colorScheme = 'gray', user }) => {
  const cardBg = useColorModeValue('white', 'gray.700');
  const defaultBorderColor = useColorModeValue('gray.200', 'gray.600');
  const borderColor = popular ? `${colorScheme}.500` : defaultBorderColor;

  return (
    <Card
      bg={cardBg}
      border="2px"
      borderColor={borderColor}
      shadow={popular ? 'xl' : 'md'}
      transform={popular ? 'scale(1.05)' : 'none'}
      position="relative"
      display="flex"
      flexDirection="column"
      h="full"
    >
      {popular && (
        <Badge
          colorScheme={colorScheme}
          position="absolute"
          top={-3}
          left="50%"
          transform="translateX(-50%)"
          px={4}
          py={1}
          borderRadius="full"
          fontSize="sm"
          fontWeight="bold"
        >
          SAVE €20
        </Badge>
      )}
      <CardHeader textAlign="center" pt={popular ? 6 : 4}>
        <Heading size="lg" color={`${colorScheme}.500`}>
          {title}
        </Heading>
        {priceSubline ? (
          <Text fontSize="md" fontWeight="semibold" color={`${colorScheme}.500`} mt={2}>
            {priceSubline}
          </Text>
        ) : (
          <Text fontSize="3xl" fontWeight="bold" color={`${colorScheme}.500`} mt={2}>
            {price === 'On-Demand' ? price : `€${price}`}
          </Text>
        )}
      </CardHeader>
      <CardBody display="flex" flexDirection="column" flex="1">
        <VStack spacing={4} align="stretch" flex="1">
          <List spacing={3} flex="1">
            {features.map((feature, index) => (
              <ListItem key={index} display="flex" alignItems="center">
                <ListIcon as={FaCheckCircle} color={`${colorScheme}.500`} />
                <Text fontSize="sm">{feature}</Text>
              </ListItem>
            ))}
          </List>
          <Button
            colorScheme={colorScheme}
            size="lg"
            w="full"
            mt="auto"
            as={RouterLink}
            to={user ? "/buy-credits" : "/create-user"}
          >
            {user ? "Manage Plan" : (ctaLabel || "Get Started")}
            <FaArrowRight ml={2} />
          </Button>
        </VStack>
      </CardBody>
    </Card>
  );
};

const LandingPage = () => {
  // const heroBg = useColorModeValue('linear(to-br, brand.500, brand.600)', 'linear(to-br, brand.500, brand.600)');
  // const heroBg = useColorModeValue('linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 'gray.800');
  const heroBg = useColorModeValue('linear-gradient(135deg, #0891b2 0%, #155e75 100%)', 'gray.800');
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const toast = useToast();
  const demoFormRef = useRef(null);
  
  const [demoForm, setDemoForm] = React.useState({
    fullName: '',
    email: '',
    company: '',
    useCase: '',
    message: ''
  });
  const [turnstileToken, setTurnstileToken] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  // Scroll to demo form
  const scrollToDemo = () => {
    demoFormRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle form input changes
  const handleDemoInputChange = (e) => {
    const { name, value } = e.target;
    setDemoForm(prev => ({
      ...prev,
      [name]: value
    }));
  };



    // Handle demo form submission
    const handleDemoSubmit = async (e) => {
      e.preventDefault();
      
      // Basic validation
      if (!demoForm.fullName || !demoForm.email) {
        toast({
          title: 'Required fields missing',
          description: 'Please fill in your name and email.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return;
      }
  
      // Check Turnstile token
      if (!turnstileToken) {
        toast({
          title: 'Verification required',
          description: 'Please complete the security check.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return;
      }
  
      setIsSubmitting(true);
      
      try {
        // Prepare payload matching your backend schema (snake_case)
        const payload = {
          full_name: demoForm.fullName,
          email: demoForm.email,
          company: demoForm.company || null,
          use_case: demoForm.useCase || null,
          message: demoForm.message || null,
          turnstile_token: turnstileToken
        };
  
  
        const response = await api.bookDemo(payload);
        
        toast({
          title: 'Demo request submitted!',
          description: "We'll contact you within 24 hours to schedule your demo.",
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        // Reset form
        setDemoForm({
          fullName: '',
          email: '',
          company: '',
          useCase: '',
          message: ''
        });
        setTurnstileToken('');
        
        // Reset Turnstile widget
        if (window.turnstile) {
          window.turnstile.reset();
        }
        
      } catch (error) {
        console.error('Demo request failed:', error);
        toast({
          title: 'Submission failed',
          description: error.response?.data?.detail || 'Please try again or contact us directly.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        
        // Reset Turnstile on error
        if (window.turnstile) {
          window.turnstile.reset();
        }
      } finally {
        setIsSubmitting(false);
      }
    };

  const features = [
    {
      icon: FaMicrochip,
      title: 'GPU Acceleration',
      description: 'Leverage the latest NVIDIA GPUs for maximum performance',
      color: 'accent.500',
    },
    {
      icon: FaCloud,
      title: 'Cloud-Based',
      description: 'Access your simulations from anywhere with our cloud platform',
      color: 'brand.500',
    },
    {
      icon: FaChartLine,
      title: 'Real-time Monitoring',
      description: 'Monitor simulation progress and performance in real-time',
      color: 'green.500',
    },
    {
      icon: FaShieldAlt,
      title: 'Secure & Reliable',
      description: 'Enterprise-grade security with 99.9% uptime guarantee',
      color: 'blue.500',
    },
    /*
    {
      icon: FaBrain,
      title: 'AI-Powered',
      description: 'Built with AI at its core to accelerate simulations, enhance accuracy, and optimize performance',
      color: 'purple.500',
    },
    */
    {
      icon: FaBolt,
      title: 'Fast Results',
      description: 'Get results in minutes, not hours with our optimized platform',
      color: 'orange.500',
    },
  ];

  const pricingPlans = [
    {
      title: 'Starter',
      price: '1',
      ctaLabel: 'Start with €1',
      features: [
        'Unlimited storage',
        '2 hours compute time',
        'Basic GPU access',
      ],
      colorScheme: 'gray',
    },
    {
      title: 'Standard',
      price: '150',
      features: [
        'Priority queue',
        'Unlimited storage',
        '10 hours compute time',
        'High-performance GPUs',
        'Email support',        
        'AI Integration'
      ],
      popular: false,
      colorScheme: 'brand',
    },
    {
      title: 'Professional',
      price: '300',
      features: [
        'Priority queue',
        'Unlimited storage',
        '22 hours compute time',
        'High-performance GPUs',
        'Email support',        
        'AI Integration'
      ],
      popular: true,
      colorScheme: 'green',
    },
    {
      title: 'Enterprise',
      price: 'On-Demand',
      features: [
        'Unlimited storage',
        'Unlimited compute time',
        'Unlimited concurrent simulations',
        'Premium GPU clusters',
        'Dedicated resources',
        'Priority support',
        'Custom integrations',
        'Advanced AI Integration'
      ],
      colorScheme: 'yellow',
    },
  ];

  const industries = [
    { label: 'Aerospace & Defence', icon: FaPlane },
    { label: 'Aeroacoustics & Noise', icon: FaVolumeUp },
    { label: 'Automotive & Motorsport', icon: FaCar },
    { label: 'Biomedical Flows', icon: FaHeartbeat },
    { label: 'Electronics Cooling Airflow', icon: FaMicrochip },
    { label: 'Academic & Research Computing', icon: FaFlask },
  ];

  return (
    <Box>
      <Helmet>
        <title>SRS | Scale-Resolving Simulation in the Cloud</title>
        <meta name="description" content="Run GPU-accelerated Scale-Resolving Simulations of turbulent industrial flows in the cloud with PyFR. AI-assisted validation and real-time visualization." />
        <link rel="canonical" href="https://srs.numericalai.net/" />
        <meta property="og:title" content="SRS | Scale-Resolving Simulation in the Cloud" />
        <meta property="og:description" content="Run GPU-accelerated Scale-Resolving Simulations of turbulent industrial flows in the cloud with PyFR. AI-assisted validation and real-time visualization." />
        <meta property="og:url" content="https://srs.numericalai.net/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SRS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SRS | Scale-Resolving Simulation in the Cloud" />
        <meta name="twitter:description" content="Run GPU-accelerated Scale-Resolving Simulations of turbulent industrial flows in the cloud with PyFR. AI-assisted validation and real-time visualization." />
      </Helmet>
      {/* Header */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200" position="sticky" top={0} zIndex={10}>
        <Container maxW="container.xl" py={4}>
          <HStack justify="space-between">
            <HStack spacing={4}>
              <Image
                src="/simple-logo.png"
                alt="SRS Logo"
                boxSize={14}
                objectFit="contain"
              />
              <Box>
                <Heading size="lg" color="brand.500">SRS</Heading>
                <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
              </Box>
            </HStack>
            <HStack spacing={4}>
              <NavDropdowns />
              {user ? (
                <Button 
                leftIcon={<FaCloud />}
                colorScheme="brand"
                size="sm"
                variant="ghost" as={RouterLink} to="/dashboard">
                  Dashboard
                </Button>
              ) : (
                <Button variant="ghost" as={RouterLink} to="/signin">
                  Sign In
                </Button>
              )}
              {user ? (
                <Button 
                  leftIcon={<FaSignOutAlt />}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    logout();
                  }}
                >
                  Sign Out
                </Button>
              ) : (
                <Button colorScheme="brand" as={RouterLink} to="/create-user">
                  Get Started
                </Button>
              )}
            </HStack>
          </HStack>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box bg={heroBg} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={8} textAlign="center">
            <Heading as="h1" size="3xl" fontWeight="bold" maxW="4xl">
              Scale-Resolving Simulations of{' '}
              <Text as="span" color="yellow.300">
              Industrial Turbulent Flows
              </Text>
            </Heading>
            <Text fontSize="xl" maxW="2xl" opacity={0.9}>
              Cloud GPU simulations powered by the {' '}
                <Link
                as={RouterLink}
                to="/learning-guide"
                fontWeight="semibold"
                color="yellow.300"
                _hover={{ textDecoration: 'underline', opacity: 0.9 }}
              >
              PyFR engine  
              </Link>                                           
               — high-order Flux Reconstruction on unstructured meshes, with zero upfront cost.
            </Text>
            <HStack
              spacing={{ base: 10, md: 14 }}
              justify="center"
              align="flex-start"
              flexWrap="wrap"
            >
              <VStack spacing={1} minW="140px">
                <Text
                  fontSize={{ base: '5xl', md: '7xl' }}
                  fontWeight="extrabold"
                  color="yellow.300"
                  lineHeight="1"
                >
                  €0
                </Text>
                <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="semibold">
                  License fees
                </Text>
                <Text fontSize="md" opacity={0.85}>
                  No annual seats
                </Text>
              </VStack>
              <VStack spacing={1} minW="140px">
                <Text
                  fontSize={{ base: '5xl', md: '7xl' }}
                  fontWeight="extrabold"
                  color="yellow.300"
                  lineHeight="1"
                >
                  0
                </Text>
                <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="semibold">
                  HPC setup time
                </Text>
                <Text fontSize="md" opacity={0.85}>
                  Start simulating in minutes
                </Text>
              </VStack>
            </HStack>
            <Stack 
              direction={{ base: 'column', md: 'row' }} 
              spacing={{ base: 4, md: 6 }}
              w={{ base: 'full', md: 'auto' }}
              align="center"
            >

              <Button
                leftIcon={<FaBookOpen />}
                variant="outline"
                color="white"
                borderColor="white"
                _hover={{ bg: 'white', color: 'brand.500' }}
                size="lg"
                onClick={() => navigate('/tutorial')}
              >
                Guide 
              </Button>

              <Button
                leftIcon={<FaCalendarAlt />}
                variant="outline"
                color="white"
                borderColor="yellow.300"
                _hover={{ bg: 'yellow.300', color: 'gray.800' }}
                size="lg"
                onClick={scrollToDemo}
              >
                Book Demo
              </Button>

              <Button
                size="lg"
                colorScheme="yellow"
                leftIcon={user ? <FaCloud /> : <FaPlay />}
                as={RouterLink}
                to={user ? "/dashboard" : "/create-user"}
              >
                {user ? "Go to Dashboard" : "Create Account"}
              </Button>
            </Stack>
          </VStack>
        </Container>
      </Box>

            
      {/* Industries Section */}
      <Box bg="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={8}>
            <VStack spacing={4} textAlign="center">
              <Heading size="2xl" color="gray.800">
                Industries We Serve
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="3xl">                              
              Built on PyFR&apos;s high-order flux reconstruction solver, our platform runs
                scale-resolving simulations of compressible and incompressible flows &mdash;
                from unsteady, separated wakes to shock-dominated transonic regimes &mdash;
                on GPU and CPU clusters alike.
              </Text>
            </VStack>
            <SimpleGrid columns={{ base: 2, md: 3 }} spacing={8}>
              {industries.map((industry) => (
                <VStack
                  key={industry.label}
                  textAlign="center"
                  spacing={4}
                  align="center"
                  justify="center"
                  h="full"
                >
                  <Icon as={industry.icon} color="brand.500" boxSize={14} />
                  <Text color="gray.600" fontSize="md" fontWeight="medium">
                    {industry.label}
                  </Text>
                </VStack>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>


      {/* Use Cases Section */}
      <Box bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading size="2xl" color="gray.800">
                Applications & Use Cases
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="2xl">
                Three solver backends — compressible flow, full-wave electromagnetics, and scale-resolving turbulence — on a GPU cloud platform.
              </Text>
            </VStack>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} align="stretch">
              {[
                {
                  icon: CfdWavesIcon,
                  title: 'Computational Fluid Dynamics',
                  desc: 'Compressible multiphase flow — shocks, cavitation, and bubble dynamics',
                  color: '#2196f3',
                  href: 'https://cmf.numericalai.net',
                },
                {
                  icon: CemPalaceIcon,
                  title: 'Computational Electromagnetics',
                  desc: 'Full-wave electromagnetic and RF simulation for antennas, resonators, and devices',
                  color: '#6366f1',
                  href: 'https://cem.numericalai.net',
                },
                {
                  icon: FaWind,
                  title: 'Scale-Resolving Turbulence',
                  desc: 'High-order LES and DNS of turbulent flows',
                  color: 'brand.500',
                },
              ].map((item, index) => (
                <Card
                key={index}
                {...(item.href
                  ? { as: Link, href: item.href, isExternal: true }
                  : {})}
                h="100%"
                p={6}
                textDecoration="none"
                transition="all 0.2s"
                {...(item.href
                  ? { _hover: { shadow: 'md', transform: 'translateY(-2px)' } }
                  : {})}
                >
                  <VStack spacing={4} align="center" h="100%">
                    <Center>
                      <HStack spacing={2}>
                        <Icon as={item.icon} color={item.color} boxSize={12} />
                      </HStack>
                    </Center>
                    <Heading size="md" textAlign="center" color={item.color}>{item.title}</Heading>
                    <Text color="gray.600" textAlign="center">
                      {item.desc}
                    </Text>
                  </VStack>
                </Card>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>
      {/* Executive Briefs Section 
      <Box bg="gray.800" py={{ base: 14, md: 16 }}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">

            // Left: copy + ROI bullets
            <VStack align="start" spacing={6}>
              <Badge colorScheme="brand" variant="solid" fontSize="xs" px={3} py={1} borderRadius="full" textTransform="none">
                For Leadership &amp; Decision-Makers
              </Badge>
              <Heading size="xl" color="white" lineHeight="shorter">
                Built for every industry
              </Heading>
              <Text color="gray.300" fontSize="md" maxW="lg">
                Six sector-specific executive briefs — covering cost benchmarks, ROI projections,
                relevant physics templates, and a 90-day adoption roadmap. Prepared for C-suite and
                engineering leadership review.
              </Text>
              <VStack align="start" spacing={3}>
                {[
                  { icon: FaClock, text: 'Same-day or few-day simulation turnarounds' },
                  { icon: FaShieldAlt, text: 'Eliminate meshing and HPC procurement bottlenecks' },
                  { icon: FaChartLine, text: '$10M+ conservative annual savings per programme, starting at €1' },
                  // { icon: FaBrain, text: 'AI Copilot turns plain-English questions into ready-to-run simulation case files' },
                  { icon: FaBolt, text: '10–100× faster than CPU-based legacy tools' },
                ].map(({ icon, text }) => (
                  <HStack key={text} spacing={3}>
                    <Icon as={icon} color="brand.400" boxSize={4} flexShrink={0} />
                    <Text color="gray.300" fontSize="sm">{text}</Text>
                  </HStack>
                ))}
              </VStack>
              <Button
                as={RouterLink}
                to="/executive-brief"
                colorScheme="brand"
                size="md"
                leftIcon={<Icon as={FaFileAlt} />}
                rightIcon={<Icon as={FaArrowRight} />}
              >
                Request an executive brief
              </Button>
            </VStack>

            // Right: industry chips
            <VStack align="start" spacing={5}>
              <Text color="gray.400" fontSize="sm" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                Available for 6 industries
              </Text>
              <SimpleGrid columns={2} spacing={3} w="full">
                {[
                  { label: 'Aerospace', icon: FaPlane, color: 'blue' },
                  { label: 'Automotive', icon: FaCar, color: 'red' },
                  { label: 'Energy', icon: FaLeaf, color: 'green' },
                  { label: 'Manufacturing', icon: FaWrench, color: 'purple' },
                  { label: 'Marine', icon: FaAnchor, color: 'teal' },
                  { label: 'Oil & gas', icon: FaOilCan, color: 'yellow' },
                ].map(({ label, icon, color }) => (
                  <Link
                    key={label}
                    as={RouterLink}
                    to="/executive-brief"
                    _hover={{ textDecoration: 'none' }}
                  >
                    <HStack
                      spacing={3}
                      bg="gray.700"
                      px={4}
                      py={3}
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor="gray.600"
                      transition="all 0.15s"
                      _hover={{ bg: 'gray.600', borderColor: `${color}.400` }}
                      cursor="pointer"
                    >
                      <Icon as={icon} color={`${color}.400`} boxSize={4} flexShrink={0} />
                      <Text color="gray.200" fontSize="sm" fontWeight="medium">{label}</Text>
                    </HStack>
                  </Link>
                ))}
              </SimpleGrid>
              <Text color="gray.500" fontSize="xs">
                Each brief is a confidential PDF prepared for executive review.
              </Text>
            </VStack>

          </SimpleGrid>
        </Container>
      </Box>*/}



      {/* Copilot Intro Section 
      <Box bg="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <HStack spacing={2}>
                <Badge colorScheme="purple" fontSize="md" px={4} py={2} borderRadius="full">
                  AI Copilot
                </Badge>
                <Badge colorScheme="orange" variant="subtle" fontSize="xs" px={2} py={1} borderRadius="full">
                  Beta
                </Badge>
              </HStack>
              <Heading size="2xl" color="gray.800">
                Your Engineering Simulation AI Assistant
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="2xl">
                Ask questions about Scale-Resolving Simulations (SRS) in plain English. Get instant answers about solver settings, boundary conditions, schemes, and even generate ready-to-run configuration files for the PyFR engine.
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} w="full" maxW="4xl">
              {[
                {
                  icon: FaCog,
                  color: 'brand',
                  title: 'Configuration Help',
                  example: '"How do I set up a 3D Taylor–Green vortex LES case?"',
                },
                {
                  icon: FaBookOpen,
                  color: 'blue',
                  title: 'Learn SRS Concepts',
                  example: '"Explain Flux Reconstruction and when to use high-order schemes"',
                },
                {
                  icon: FaPlay,
                  color: 'green',
                  title: 'Generate Case Files',
                  example: '"Generate a PyFR .ini for a low-pressure turbine cascade"',
                },
                {
                  icon: FaBolt,
                  color: 'orange',
                  title: 'Schemes & Polynomial Order',
                  example: '"What polynomial order should I use for my mesh?"',
                },
                {
                  icon: FaRocket,
                  color: 'red',
                  title: 'Performance & GPU',
                  example: '"How do I enable GPU acceleration for my simulation?"',
                },
                {
                  icon: FaShieldAlt,
                  color: 'purple',
                  title: 'Boundary Conditions',
                  example: '"What boundary conditions are available in the SRS solver?"',
                },
              ].map((item, i) => (
                <Card
                  key={i}
                  shadow="sm"
                  border="1px"
                  borderColor="gray.200"
                  transition="all 0.2s"
                  _hover={{ shadow: 'md', borderColor: 'purple.200' }}
                >
                  <CardBody p={5}>
                    <VStack spacing={3} align="start">
                      <Icon as={item.icon} color={`${item.color}.500`} boxSize={6} />
                      <Text fontWeight="semibold" color="gray.800">{item.title}</Text>
                      <Text fontSize="sm" color="gray.500" fontStyle="italic">{item.example}</Text>
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
            <Text fontSize="md" color="gray.600" mt={3} maxW="2xl" textAlign="center">
              Register now and try using the copilot to help expedite your learning journey.
              <br />
              <Link as={RouterLink} to="/copilot-intro" color="purple.500" fontWeight="semibold">Learn more about SRS Copilot →</Link>
            </Text>

          </VStack>
        </Container>
      </Box>*/}

      {/* Example Templates Section */}
      <Box bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Badge colorScheme="brand" fontSize="md" px={4} py={2} borderRadius="full">
                Ready-to-Run
              </Badge>
              <Heading size="2xl" color="gray.800">
                Launch Templates
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="2xl">
                Browse real SRS case files covering aerospace, turbomachinery, wind energy, and more — learn how the solver works and run them instantly.
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} w="full">
              {[
                { title: 'External Aerodynamics & Bluff Bodies', desc: 'Flow over aerofoils and bluff bodies with force monitoring and wake resolution', icon: FaPlane, color: 'cyan' },
                { title: 'High-Speed Compressible & Shock Capturing', desc: 'Strong-shock and supersonic problems using entropy-filter shock capturing', icon: FaBolt, color: 'red' },
                { title: 'Turbulence, Transition & LES', desc: 'Scale-resolving simulations of turbulence decay, transition, and turbulent wakes', icon: FaRocket, color: 'pink' },
                { title: 'Vortex Dynamics & Rotational Flows', desc: 'Vortex transport and rotational-flow benchmarks for high-order solvers', icon: FaCircleNotch, color: 'blue' },
              ].map((item, i) => (
                <Card
                  key={i}
                  shadow="md"
                  border="1px"
                  borderColor="gray.200"
                  transition="all 0.2s"
                  _hover={{ shadow: 'xl', transform: 'translateY(-4px)' }}
                  cursor="pointer"
                  as={RouterLink}
                  to="/examples"
                  textDecoration="none"
                >
                  <CardBody p={6}>
                    <VStack spacing={3} align="start">
                      <Icon as={item.icon} color={`${item.color}.500`} boxSize={8} />
                      <Heading size="sm" color="gray.800">{item.title}</Heading>
                      <Text fontSize="sm" color="gray.600">{item.desc}</Text>
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>

              <Text fontSize="md" color="gray.600" mt={3} maxW="2xl" textAlign="center">
              Explore validated SRS case studies with full configuration files, solver settings, and detailed write-ups — ready to run on our cloud infrastructure in minutes.
              <br />
              <Link as={RouterLink} to="/examples" color="brand.500" fontWeight="semibold">Explore all examples →</Link>
              <br />
              <Link as={RouterLink} to="/executive-brief" color="brand.500" fontWeight="semibold">
                Request an industry executive brief by email →
              </Link>
            </Text>


          </VStack>
        </Container>
      </Box>

      {/* Featured Simulations Section 
      <Box bg="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Badge colorScheme="purple" fontSize="md" px={4} py={2} borderRadius="full">
                Case Studies
              </Badge>
              <Heading size="2xl" color="gray.800">
                Featured Simulations
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="2xl">
                Explore real-world simulation examples showcasing the power of GPU-accelerated Scale-Resolving Simulation on SRS
              </Text>
            </VStack>
            
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8} w="full">
              // Card 1: Multiphase Acoustics 
              <Card
                as={RouterLink}
                to="/blog/multiphase-acoustics-bubble-screen"
                overflow="hidden"
                cursor="pointer"
                transition="all 0.3s"
                _hover={{ 
                  transform: 'translateY(-8px)', 
                  shadow: '2xl',
                  '& .overlay': {
                    opacity: 1
                  }
                }}
                position="relative"
              >
                <Box position="relative" overflow="hidden" h="240px">
                  <Image
                    src={require('../img/blogs/3D_lagrange_bubblescreen_Void_fraction_time_0_33s_NumericalAI.jpg')}
                    alt="3D Multiphase Acoustics Bubble Screen"
                    w="100%"
                    h="100%"
                    objectFit="cover"
                  />
                  <Box
                    className="overlay"
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    bg="blackAlpha.700"
                    opacity={0}
                    transition="opacity 0.3s"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Button colorScheme="brand" size="lg" rightIcon={<FaArrowRight />}>
                      View Details
                    </Button>
                  </Box>
                </Box>
                <CardBody>
                  <VStack spacing={3} align="start">
                    <Badge colorScheme="blue" fontSize="sm">
                      Multiphase Flow
                    </Badge>
                    <Heading size="md" color="gray.800">
                    High-Fidelity 3D Multiphase Acoustics
                    </Heading>
                    <Text color="gray.600" fontSize="sm" noOfLines={3}>
                      High-fidelity simulation of acoustic wave propagation through dense bubble clouds using hybrid Eulerian-Lagrangian methods.
                    </Text>
                    <HStack spacing={2} pt={2}>
                      <Icon as={FaClock} color="gray.500" boxSize={4} />
                      <Text fontSize="xs" color="gray.500">~4.2 hours on A100</Text>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>

              // Card 2: Shock Water Cavity
              <Card
                as={RouterLink}
                to="/blog/shock-water-cavity-interaction"
                overflow="hidden"
                cursor="pointer"
                transition="all 0.3s"
                _hover={{ 
                  transform: 'translateY(-8px)', 
                  shadow: '2xl',
                  '& .overlay': {
                    opacity: 1
                  }
                }}
                position="relative"
              >
                <Box position="relative" overflow="hidden" h="240px">
                  <Image
                    src={require('../img/blogs/shock_water_cavity_density_contour_time_0-36s_CFD_Simulation_NumericalAI.jpg')}
                    alt="Shock Water Cavity Interaction"
                    w="100%"
                    h="100%"
                    objectFit="cover"
                  />
                  <Box
                    className="overlay"
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    bg="blackAlpha.700"
                    opacity={0}
                    transition="opacity 0.3s"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Button colorScheme="brand" size="lg" rightIcon={<FaArrowRight />}>
                      View Details
                    </Button>
                  </Box>
                </Box>
                <CardBody>
                  <VStack spacing={3} align="start">
                    <Badge colorScheme="red" fontSize="sm">
                      Shock Dynamics
                    </Badge>
                    <Heading size="md" color="gray.800">
                    Advanced CFD Insights for Fluid-Structure and Multiphase Dynamics
                    </Heading>
                    <Text color="gray.600" fontSize="sm" noOfLines={3}>
                      Axisymmetric simulation capturing high-speed shock wave interaction with water droplets using compressible multi-fluid methods.
                    </Text>
                    <HStack spacing={2} pt={2}>
                      <Icon as={FaClock} color="gray.500" boxSize={4} />
                      <Text fontSize="xs" color="gray.500">~2.8 hours on A100</Text>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>

              // Card 3: Gresho Vortex Validation
              <Card
                as={RouterLink}
                to="/blog/gresho-vortex-validation"
                overflow="hidden"
                cursor="pointer"
                transition="all 0.3s"
                _hover={{ 
                  transform: 'translateY(-8px)', 
                  shadow: '2xl',
                  '& .overlay': {
                    opacity: 1
                  }
                }}
                position="relative"
              >
                <Box position="relative" overflow="hidden" h="240px">
                  <Image
                    src={require('../img/blogs/2D_GreshoVortex_pressure_animation_NumericalAI-thumbnail.gif')}
                    alt="Gresho Vortex Validation"
                    w="100%"
                    h="100%"
                    objectFit="cover"
                  />
                  <Box
                    className="overlay"
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    bg="blackAlpha.700"
                    opacity={0}
                    transition="opacity 0.3s"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Button colorScheme="brand" size="lg" rightIcon={<FaArrowRight />}>
                      View Details
                    </Button>
                  </Box>
                </Box>
                <CardBody>
                  <VStack spacing={3} align="start">
                    <Badge colorScheme="green" fontSize="sm">
                      Validation Study
                    </Badge>
                    <Heading size="md" color="gray.800">
                    Accurate Vortex Dynamics for Industrial Flow Design
                    </Heading>
                    <Text color="gray.600" fontSize="sm" noOfLines={3}>
                      Classic validation case demonstrating solver accuracy and stability through long-time vortex preservation with zero numerical dissipation.
                    </Text>
                    <HStack spacing={2} pt={2}>
                      <Icon as={FaClock} color="gray.500" boxSize={4} />
                      <Text fontSize="xs" color="gray.500">~18 min on A100</Text>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>

                  

            // View All Button
            <Box textAlign="center" pt={4}>
              <Text fontSize="md" color="gray.600">
                More case studies coming soon. Stay tuned for advanced simulations in structural mechanics, multiphysics, and molecular dynamics.
              </Text>
            </Box>
          </VStack>
        </Container>
      </Box>*/}


      {/* Comparison Section - Us vs Them */}
      <Box bg="white" py={20} position="relative" overflow="hidden">
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Badge colorScheme="brand" fontSize="md" px={4} py={2} borderRadius="full">
                Competitive Advantage
              </Badge>
              <Heading size="2xl" color="gray.800">
                SRS vs Traditional Tools
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="2xl">
                Unlike traditional simulation platforms, SRS is built from the ground up with GPU acceleration and AI-driven algorithms on top of the high-order PyFR engine. This combination drastically reduces simulation time, minimizes errors, and enhances efficiency and productivity.
                See how we stack up against traditional simulation platforms
              </Text>
            </VStack>

            {/* Comparison Table */}
            <Box maxW="5xl" mx="auto" w="full">
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={0} borderWidth="1px" borderColor="gray.200" borderRadius="xl" overflow="hidden">
                {/* Header Row */}
                <Box bg="gray.50" p={6} borderBottomWidth="2px" borderColor="gray.200">
                </Box>
                <Box bg="brand.500" p={6} borderBottomWidth="2px" borderColor="brand.600">
                  <VStack spacing={2}>
                    <Icon as={FaRocket} color="white" boxSize={6} />
                    <Text fontWeight="bold" fontSize="lg" color="white">
                      SRS
                    </Text>
                  </VStack>
                </Box>
                <Box bg="gray.100" p={6} borderBottomWidth="2px" borderColor="gray.300">
                  <VStack spacing={2}>
                    <Icon as={FaServer} color="gray.600" boxSize={6} />
                    <Text fontWeight="bold" fontSize="lg" color="gray.700">
                      Traditional Tools
                    </Text>
                  </VStack>
                </Box>

                {/* GPU Support Row */}
                <Box p={6} bg="gray.50" borderBottomWidth="1px" borderColor="gray.200" display="flex" alignItems="center">
                  <HStack>
                    <Icon as={FaMicrochip} color="brand.500" boxSize={5} />
                    <Text fontWeight="semibold" color="gray.700" fontSize="xl">GPU Support</Text>
                  </HStack>
                </Box>
                <Box p={6} bg="brand.50" borderBottomWidth="1px" borderColor="gray.200" display="flex" alignItems="center" justifyContent="center">
                  <VStack spacing={1}>
                    <Icon as={FaCheckCircle} color="green.500" boxSize={6} />
                    <Text fontWeight="semibold" color="gray.700" textAlign="center">
                      Native NVIDIA A/H100
                    </Text>
                  </VStack>
                </Box>
                <Box p={6} bg="white" borderBottomWidth="1px" borderColor="gray.200" display="flex" alignItems="center" justifyContent="center">
                  <VStack spacing={1}>
                    <Icon as={FaTimes} color="red.500" boxSize={6} />
                    <Text color="gray.600" textAlign="center">CPU-only or limited</Text>
                  </VStack>
                </Box>

                {/* Pricing Row */}
                <Box p={6} bg="gray.50" borderBottomWidth="1px" borderColor="gray.200" display="flex" alignItems="center">
                  <HStack>
                    <Icon as={FaCreditCard} color="brand.500" boxSize={5} />
                    <Text fontWeight="semibold" color="gray.700" fontSize="xl">Pricing</Text>
                  </HStack>
                </Box>
                <Box p={6} bg="brand.50" borderBottomWidth="1px" borderColor="gray.200" display="flex" alignItems="center" justifyContent="center">
                  <VStack spacing={1}>
                    <Icon as={FaCheckCircle} color="green.500" boxSize={6} />
                    <Text fontWeight="semibold" color="gray.700" textAlign="center">
                      Pay-as-you-go
                    </Text>
                    <Text fontSize="sm" color="gray.600" textAlign="center">
                      From €150/10 hours
                    </Text>
                  </VStack>
                </Box>
                <Box p={6} bg="white" borderBottomWidth="1px" borderColor="gray.200" display="flex" alignItems="center" justifyContent="center">
                  <VStack spacing={1}>
                    <Icon as={FaTimes} color="red.500" boxSize={6} />
                    <Text color="gray.600" textAlign="center">Expensive licenses</Text>
                    <Text fontSize="sm" color="gray.600" textAlign="center">
                      €50,000+ annually
                    </Text>
                  </VStack>
                </Box>

                {/* AI Assisted Row — temporarily hidden (Copilot)
                <Box p={6} bg="gray.50" borderBottomWidth="1px" borderColor="gray.200" display="flex" alignItems="center">
                  <HStack>
                    <Icon as={FaBrain} color="brand.500" boxSize={5} />
                    <Text fontWeight="semibold" fontSize="xl" color="gray.700">AI-Assistance</Text>
                  </HStack>
                </Box>
                <Box p={6} bg="brand.50" borderBottomWidth="1px" borderColor="gray.200" display="flex" alignItems="center" justifyContent="center">
                  <VStack spacing={1}>
                    <Icon as={FaCheckCircle} color="green.500" boxSize={6} />
                    <Text fontWeight="semibold" color="gray.700" textAlign="center">
                      Built-in AI
                    </Text>
                    <Text fontSize="sm" color="gray.600" textAlign="center">
                      Smart optimization
                    </Text>
                  </VStack>
                </Box>
                <Box p={6} bg="white" borderBottomWidth="1px" borderColor="gray.200" display="flex" alignItems="center" justifyContent="center">
                  <VStack spacing={1}>
                    <Icon as={FaTimes} color="red.500" boxSize={6} />
                    <Text color="gray.600" textAlign="center">Limited features</Text>
                  </VStack>
                </Box>
                */}

                {/* Storage Row */}
                <Box p={6} bg="gray.50" borderBottomWidth="1px" borderColor="gray.200" display="flex" alignItems="center">
                  <HStack>
                    <Icon as={FaDatabase} color="brand.500" boxSize={5} />
                    <Text fontWeight="semibold" color="gray.700" fontSize="xl">Storage</Text>
                  </HStack>
                </Box>
                <Box p={6} bg="brand.50" borderBottomWidth="1px" borderColor="gray.200" display="flex" alignItems="center" justifyContent="center">
                  <VStack spacing={1}>
                    <Icon as={FaCheckCircle} color="green.500" boxSize={6} />
                    <Text fontWeight="semibold" color="gray.700" textAlign="center">
                      Unlimited
                    </Text>
                    <Text fontSize="sm" color="gray.600" textAlign="center">
                      All plans
                    </Text>
                  </VStack>
                </Box>
                <Box p={6} bg="white" borderBottomWidth="1px" borderColor="gray.200" display="flex" alignItems="center" justifyContent="center">
                  <VStack spacing={1}>
                    <Icon as={FaTimes} color="red.500" boxSize={6} />
                    <Text color="gray.600" textAlign="center">Limited storage</Text>
                    <Text fontSize="sm" color="gray.600" textAlign="center">
                      Extra costs
                    </Text>
                  </VStack>
                </Box>

                {/* Free Credits Row */}
                <Box p={6} bg="gray.50" display="flex" alignItems="center">
                  <HStack>
                    <Icon as={FaRocket} color="brand.500" boxSize={5} />
                    <Text fontWeight="semibold" color="gray.700" fontSize="xl">Speed</Text>
                  </HStack>
                </Box>
                <Box p={6} bg="brand.50" display="flex" alignItems="center" justifyContent="center">
                  <VStack spacing={1}>
                    <Icon as={FaCheckCircle} color="green.500" boxSize={6} />
                    <Text fontWeight="semibold" color="gray.700" textAlign="center">
                      Up to 10x faster
                    </Text>
                  </VStack>
                </Box>
                <Box p={6} bg="white" display="flex" alignItems="center" justifyContent="center">
                  <VStack spacing={1}>
                    <Icon as={FaTimes} color="red.500" boxSize={6} />
                    <Text color="gray.600" textAlign="center">Slower (CPU only)</Text>
                  </VStack>
                </Box>
              </SimpleGrid>

              {/* CTA Below Table */}
              <Box textAlign="center" mt={8}>
                
                <Text fontSize="md" color="gray.600" mt={3}>
                  Start with the €1 Starter — 2 hours of GPU compute included.{' '}
                  <br />
                  <Link as={RouterLink} to={user ? "/dashboard" : "/create-user"} color="brand.500" fontWeight="semibold">Start Simulation →</Link>
                </Text>
              </Box>
            </Box>
          </VStack>
        </Container>
      </Box>


      {/* Features Section */}
      <Box bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading size="2xl" color="gray.800">
                Why Choose SRS?
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="2xl">
              Our platform combines the high-order PyFR engine, cutting-edge GPU acceleration, and intuitive AI tools to make scale-resolving simulation accessible to engineers worldwide.
              </Text>
            </VStack>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
              {features.map((feature, index) => (
                <FeatureCard key={index} {...feature} />
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>


      {/* Pricing Section */}
      <Box bg="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading size="2xl" color="gray.800">
                Simple, Transparent Usage-Based Pricing
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="2xl">
                Choose the plan that fits your needs. All plans are one-time purchases.
              </Text>
            </VStack>

            {/* Copilot Credits Callout 
            <Box
              w="full"
              maxW="4xl"
              mx="auto"
              bgGradient="linear(to-r, purple.50, purple.100)"
              borderRadius="2xl"
              border="1px"
              borderColor="purple.200"
              p={{ base: 6, md: 8 }}
              shadow="sm"
            >
              <Stack direction={{ base: 'column', md: 'row' }} spacing={6} align="center" justify="space-between">
                <HStack spacing={5} flex="1">
                  <Box bg="purple.500" color="white" borderRadius="full" p={4} flexShrink={0}>
                    <Icon as={FaRobot} boxSize={8} />
                  </Box>
                  <VStack align="start" spacing={1}>
                    <HStack spacing={2}>
                      <Badge colorScheme="purple" variant="solid" fontSize="xs">BETA</Badge>
                      <Heading size="md" color="purple.800">AI Copilot Credits</Heading>
                    </HStack>
                    <Text color="gray.700" fontSize="md">
                      Generate simulation configuration files from plain English, validate automatically, and ask SRS solver questions.
                    </Text>
                    <HStack spacing={3} pt={1} flexWrap="wrap">
                      <Text fontSize="2xl" fontWeight="bold" color="purple.700">€70</Text>
                      <Text fontSize="sm" color="gray.500">one-time &middot; usage-based &middot; transparent pricing</Text>
                    </HStack>
                  </VStack>
                </HStack>
                <VStack spacing={2} flexShrink={0}>
                  <Button
                    colorScheme="purple"
                    size="md"
                    leftIcon={<FaCoins />}
                    as={RouterLink}
                    to={user ? '/buy-credits' : '/create-user'}
                    minW="180px"
                  >
                    {user ? 'Buy Copilot Credits' : 'Get Started'}
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    colorScheme="purple"
                    as={RouterLink}
                    to="/copilot-intro"
                    rightIcon={<Icon as={FaBrain} />}
                  >
                    Learn about Copilot
                  </Button>
                </VStack>
              </Stack>
            </Box>
*/}
            {/* Simulation Pricing Tiers */}
            <VStack spacing={4} textAlign="center" pt={4}>
              <Heading size="lg" color="gray.800">Simulation Credits</Heading>
              <Text fontSize="md" color="gray.600" maxW="2xl">
                One-time credit packs for running SRS simulations on our GPU cloud.
              </Text>
            </VStack>
            <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4} w="full">
              {pricingPlans.map((plan, index) => (
                <PricingCard key={index} {...plan} user={user} />
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Book Demo Section */}
      <Box ref={demoFormRef} bg="gray.50" py={20}>
        <Container maxW="container.md">
          <VStack spacing={8}>
            <VStack spacing={4} textAlign="center">
              <Badge colorScheme="brand" fontSize="md" px={4} py={2} borderRadius="full">
                Enterprise & Professional
              </Badge>
              <Heading size="2xl" color="gray.800">
                Book a Demo
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="2xl">
                Want a personalized walkthrough? Schedule a demo with our team to see how SRS can accelerate your scale-resolving simulations.
              </Text>
            </VStack>
            <Box
              as="form"
              onSubmit={handleDemoSubmit}
              w="full"
              bg="white"
              p={8}
              borderRadius="xl"
              shadow="lg"
              border="1px"
              borderColor="gray.200"
            >
              <VStack spacing={5}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} w="full">
                  <FormControl isRequired>
                    <FormLabel color="gray.700">Full Name</FormLabel>
                    <Input
                      name="fullName"
                      value={demoForm.fullName}
                      onChange={handleDemoInputChange}
                      placeholder="John Doe"
                      size="lg"
                      bg="gray.50"
                      border="1px"
                      borderColor="gray.300"
                      _hover={{ borderColor: 'brand.400' }}
                      _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                    />
                  </FormControl>
                  
                  <FormControl isRequired>
                    <FormLabel color="gray.700">Work Email</FormLabel>
                    <Input
                      name="email"
                      type="email"
                      value={demoForm.email}
                      onChange={handleDemoInputChange}
                      placeholder="john@company.com"
                      size="lg"
                      bg="gray.50"
                      border="1px"
                      borderColor="gray.300"
                      _hover={{ borderColor: 'brand.400' }}
                      _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                    />
                  </FormControl>
                </SimpleGrid>
                
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} w="full">
                  <FormControl>
                    <FormLabel color="gray.700">Company</FormLabel>
                    <Input
                      name="company"
                      value={demoForm.company}
                      onChange={handleDemoInputChange}
                      placeholder="Company Name"
                      size="lg"
                      bg="gray.50"
                      border="1px"
                      borderColor="gray.300"
                      _hover={{ borderColor: 'brand.400' }}
                      _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                    />
                  </FormControl>
                  
                  <FormControl>
                    <FormLabel color="gray.700">Use Case</FormLabel>
                    <Select
                      name="useCase"
                      value={demoForm.useCase}
                      onChange={handleDemoInputChange}
                      placeholder="Select a use case"
                      size="lg"
                      bg="gray.50"
                      border="1px"
                      borderColor="gray.300"
                      _hover={{ borderColor: 'brand.400' }}
                      _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                    >
                      <option value="fluid-dynamics">Computational Fluid Dynamics (CFD)</option>
                      <option value="enterprise">Computational Electromagnetics</option>
                      <option value="enterprise">Scale-Resolving Turbulence</option>
                      <option value="structural">Structural Mechanics</option>
                      <option value="multiphysics">Multiphysics</option>
                      <option value="molecular">Molecular Dynamics</option>
                      <option value="enterprise">Enterprise Solutions</option>
                      <option value="other">Other</option>
                    </Select>
                  </FormControl>
                </SimpleGrid>
                
                <FormControl>
                  <FormLabel color="gray.700">Message</FormLabel>
                  <Textarea
                    name="message"
                    value={demoForm.message}
                    onChange={handleDemoInputChange}
                    placeholder="Tell us about your simulation needs, team size, or any specific questions..."
                    rows={4}
                    size="lg"
                    bg="gray.50"
                    border="1px"
                    borderColor="gray.300"
                    _hover={{ borderColor: 'brand.400' }}
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                    resize="vertical"
                  />
                </FormControl>

                {/* Turnstile Widget */}
                <Box w="full" display="flex" justifyContent="center">
                  <Turnstile
                    sitekey={TURNSTILE_SITE_KEY}
                    onVerify={(token) => setTurnstileToken(token)}
                  />
                </Box>
                
                <Button
                  type="submit"
                  colorScheme="brand"
                  size="lg"
                  w="full"
                  leftIcon={<FaCalendarAlt />}
                  isLoading={isSubmitting}
                  loadingText="Submitting..."
                  isDisabled={!turnstileToken}
                >
                  Request Demo
                </Button>
                
                <Text fontSize="sm" color="gray.500" textAlign="center">
                  We'll get back to you within 24 hours to schedule your personalized demo.
                </Text>
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>


      {/* FAQ Section */}
      <Box bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading size="2xl" color="gray.800">
                Frequently Asked Questions
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="2xl">
                Quick answers to common questions about SRS
              </Text>
            </VStack>
            <Box maxW="4xl" mx="auto" w="full">
              <Accordion allowMultiple>
                <AccordionItem border="1px" borderColor="gray.200" borderRadius="md" mb={3}>
                  <AccordionButton py={4} _hover={{ bg: 'gray.50' }}>
                    <Box flex="1" textAlign="left">
                      <Text fontWeight="semibold" color="gray.800" fontSize="md">
                        Why SRS — what makes it special?
                      </Text>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4} pt={2} bg="gray.50">
                    <Text color="gray.600" lineHeight="1.8">
                      Unlike traditional simulation tools, SRS is built on the high-order PyFR engine, GPU acceleration, and AI-driven preflight validation. This combination reduces setup errors and speeds up scale-resolving turbulence simulations (LES, DNS, ILES) for industrial flow problems.
                    </Text>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem border="1px" borderColor="gray.200" borderRadius="md" mb={3}>
                  <AccordionButton py={4} _hover={{ bg: 'gray.50' }}>
                    <Box flex="1" textAlign="left">
                      <Text fontWeight="semibold" color="gray.800" fontSize="md">
                        What types of simulations can I run?
                      </Text>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4} pt={2} bg="gray.50">
                    <Text color="gray.600" lineHeight="1.8">
                      You can run Scale-Resolving Simulations with PyFR — LES, DNS, and ILES on unstructured mixed-element meshes in 2D and 3D. Typical applications include external aerodynamics, turbomachinery, wind-turbine wakes, and vortex-dominated flows, all accelerated on NVIDIA A100/H100 GPUs.
                    </Text>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem border="1px" borderColor="gray.200" borderRadius="md" mb={3}>
                  <AccordionButton py={4} _hover={{ bg: 'gray.50' }}>
                    <Box flex="1" textAlign="left">
                      <Text fontWeight="semibold" color="gray.800" fontSize="md">
                        What is the difference between €1 Starter and Premium users?
                      </Text>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4} pt={2} bg="gray.50">
                    <Text color="gray.600" lineHeight="1.8">
                      New users activate their account with the €1 Starter plan, which includes two hours of compute time and basic GPU access. Purchasing Standard, Professional, or additional compute credits upgrades you to On Demand Usage (Premium), with priority queue access, advanced AI-assisted validation, and debugging tools.
                    </Text>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem border="1px" borderColor="gray.200" borderRadius="md" mb={3}>
                  <AccordionButton py={4} _hover={{ bg: 'gray.50' }}>
                    <Box flex="1" textAlign="left">
                      <Text fontWeight="semibold" color="gray.800" fontSize="md">
                        How fast is SRS?
                      </Text>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4} pt={2} bg="gray.50">
                    <Text color="gray.600" lineHeight="1.8">
                      When benchmarked against a 32-core CPU system, SRS achieves up to 10× faster performance on common PyFR workloads such as high-fidelity turbulence simulations. GPU parallelization and PyFR's CUDA backend make this possible.
                    </Text>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem border="1px" borderColor="gray.200" borderRadius="md">
                  <AccordionButton py={4} _hover={{ bg: 'gray.50' }}>
                    <Box flex="1" textAlign="left">
                      <Text fontWeight="semibold" color="gray.800" fontSize="md">
                        What is the Enterprise subscription?
                      </Text>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4} pt={2} bg="gray.50">
                    <Text color="gray.600" lineHeight="1.8">
                      The Enterprise plan offers unlimited compute time with dedicated H100/A100 GPUs and queue priority, allowing unlimited parallel PyFR simulations without waiting, plus tailored support for large-scale SRS workloads.
                    </Text>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
              
              <Box textAlign="center" mt={8}>
                <Text fontSize="md" color="gray.600" mt={3}>

                <Link
                  as={RouterLink}
                  to="/faq"
                  variant="outline"
                  color="brand.500"
                  size="lg"
                  rightIcon={<FaArrowRight />}
                >
                  View All FAQs
                </Link>
                </Text>
              </Box>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box bg="brand.500" color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={8} textAlign="center">
            <Heading size="2xl">
              Ready to Accelerate Your Product Development?
            </Heading>
            <Text fontSize="xl" maxW="2xl" opacity={0.9}>
              Thousands of engineers are already accelerating product development with GPU- and AI-powered simulations, delivering better products faster and at lower cost.
            </Text>
            <Stack 
              direction={{ base: 'column', md: 'row' }} 
              spacing={{ base: 4, md: 6 }}
              w={{ base: 'full', md: 'auto' }}
              align="center"
            >

              <Button
                  leftIcon={<FaBookOpen />}
                  variant="outline"
                  color="white"
                  borderColor="white"
                  _hover={{ bg: 'white', color: 'brand.500' }}
                  size="lg"
                  onClick={() => navigate('/tutorial')}
                >
                  Guide 
              </Button>

              <Button
                size="lg"
                colorScheme="yellow"
                leftIcon={user ? <FaCloud /> : <FaArrowRight />}
                as={RouterLink}
                to={user ? "/dashboard" : "/create-user"}
              >
                {user ? "Go to Dashboard" : "Create Account"}
              </Button>
              {!user && (
                <Button
                  size="lg"
                  variant="outline"
                  color="white"
                  borderColor="white"
                  _hover={{ bg: 'white', color: 'brand.500' }}
                  as={RouterLink}
                  to="/signin"
                >
                  Sign In
                </Button>
              )}
            </Stack>
          </VStack>
        </Container>
      </Box>

      {/* Footer */}

      <Footer />
      
    </Box>
  );
};

export default LandingPage; 